import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import {
  pushSalesOrdersToDemandApi,
  listBatchesApi,
  type DemandBatch,
  type PushSalesOrdersToDemandInput,
} from '@/services/demand'
import { formatDocumentError } from '@/utils/documentErrorMessages'
import type { ProductionOrder } from '@/services/productionOrders'

export interface ProductionOrderPushToDemandModalProps {
  isOpen: boolean
  order: ProductionOrder | null
  onClose: () => void
  onSuccess: (batchId: string) => void
}

export function ProductionOrderPushToDemandModal({
  isOpen,
  order,
  onClose,
  onSuccess,
}: ProductionOrderPushToDemandModalProps): React.ReactElement | null {
  const [mode, setMode] = useState<'new' | 'existing'>('new')
  const [newTitle, setNewTitle] = useState('')
  const [selectedBatchId, setSelectedBatchId] = useState('')
  const [batches, setBatches] = useState<DemandBatch[]>([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [errorObj, setErrorObj] = useState<any>(null)

  useEffect(() => {
    if (!isOpen || !order) return

    setMode('new')
    setNewTitle(`Đợt nhu cầu ${order.variant_code} - ${order.customer_code || ''}`.trim())
    setSelectedBatchId('')
    setErrorObj(null)

    setLoading(true)
    listBatchesApi()
      .then((res) => {
        setBatches(res)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [isOpen, order])

  if (!isOpen || !order) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!order.sales_order_line_id) {
      setErrorObj({ message: 'Dòng kế hoạch không có liên kết dòng đơn bán hàng.' })
      return
    }

    if (mode === 'new' && !newTitle.trim()) {
      setErrorObj({ message: 'Vui lòng nhập tên đợt nhu cầu mới.' })
      return
    }

    if (mode === 'existing' && !selectedBatchId) {
      setErrorObj({ message: 'Vui lòng chọn một đợt nhu cầu.' })
      return
    }

    setSubmitting(true)
    setErrorObj(null)

    try {
      const payload: PushSalesOrdersToDemandInput = {
        target:
          mode === 'new'
            ? { mode: 'create_new', title: newTitle.trim() }
            : { mode: 'existing', batch_id: selectedBatchId },
        lines: [
          {
            sales_order_line_id: order.sales_order_line_id,
            quantity: order.planned_qty || undefined,
          },
        ],
      }

      const res = await pushSalesOrdersToDemandApi(payload)
      onSuccess(res.batch_id)
      onClose()
    } catch (err: any) {
      setErrorObj(err)
    } finally {
      setSubmitting(false)
    }
  }

  const formattedErr = errorObj ? formatDocumentError(errorObj) : null

  return (
    <div className="fixed inset-0 z-[9999999] flex items-center justify-center bg-slate-900/60 p-4 animate-in fade-in duration-150">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-2xl overflow-hidden animate-in zoom-in-95 duration-150">
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-5 py-3.5">
          <h3 className="font-bold text-slate-800 text-base">Đưa vào đợt nhu cầu vật tư</h3>
          <button
            type="button"
            className="text-slate-400 hover:text-slate-600 text-lg leading-none"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700 space-y-1">
            <div className="flex justify-between">
              <span className="text-slate-500">Mã hàng:</span>
              <span className="font-mono font-bold text-slate-900">{order.variant_code}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Khách hàng:</span>
              <span className="font-medium text-slate-900">{order.customer_code || '—'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">SL kế hoạch:</span>
              <span className="font-mono font-bold text-slate-900">
                {Number(order.planned_qty || 0).toLocaleString('vi-VN')}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-slate-800">
                <input
                  type="radio"
                  name="demand_mode"
                  checked={mode === 'new'}
                  onChange={() => setMode('new')}
                  className="text-emerald-600 focus:ring-emerald-500"
                />
                <span>Tạo đợt nhu cầu mới</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-slate-800">
                <input
                  type="radio"
                  name="demand_mode"
                  checked={mode === 'existing'}
                  onChange={() => setMode('existing')}
                  className="text-emerald-600 focus:ring-emerald-500"
                />
                <span>Gộp vào đợt đã có</span>
              </label>
            </div>

            {mode === 'new' ? (
              <div className="pt-2">
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1">
                  Tên đợt nhu cầu <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="Ví dụ: Đợt SX tháng 10..."
                  className="w-full rounded border border-slate-300 p-2 text-sm text-slate-900 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
                  autoFocus
                />
              </div>
            ) : (
              <div className="pt-2">
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1">
                  Chọn đợt nhu cầu <span className="text-rose-500">*</span>
                </label>
                {loading ? (
                  <p className="text-xs text-slate-500 italic py-2">Đang tải danh sách đợt...</p>
                ) : batches.length === 0 ? (
                  <p className="text-xs text-amber-700 py-2">
                    Không có đợt nhu cầu nào đang mở. Hãy chọn tạo đợt mới.
                  </p>
                ) : (
                  <select
                    value={selectedBatchId}
                    onChange={(e) => setSelectedBatchId(e.target.value)}
                    className="w-full rounded border border-slate-300 p-2 text-sm text-slate-900 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
                  >
                    <option value="">-- Chọn đợt --</option>
                    {batches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.title || b.id}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>

          {formattedErr && (
            <div className="rounded-md border border-rose-300 bg-rose-50 p-3 text-xs text-rose-800 space-y-1">
              <p className="font-semibold">{formattedErr.message}</p>
              {formattedErr.link && (
                <Link
                  to={formattedErr.link.url}
                  className="inline-block text-emerald-700 underline hover:text-emerald-800"
                >
                  {formattedErr.link.label}
                </Link>
              )}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
            <Button variant="secondary" size="sm" type="button" onClick={onClose} disabled={submitting}>
              Hủy
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="submit"
              disabled={submitting}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
            >
              {submitting ? 'Đang đưa vào...' : 'Xác nhận đưa vào'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
